import {
  CreateMeetingRequest,
  CreateMeetingResponse,
  GetMeetingResponse,
  GetParticipantsResponse,
  GetRecordingsResponse,
  StoredMeeting,
  ZoomIntegrationStatus,
} from '../types/zoom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

class ZoomService {
  async startOAuth(): Promise<{ authorizationUrl: string }> {
    try {
      const startZoomOAuth = httpsCallable(functions, 'startZoomOAuth');
      const result = await startZoomOAuth({});
      return result.data as { authorizationUrl: string };
    } catch (error: any) {
      console.error('خطأ في بدء ربط Zoom:', error);
      throw new Error(error.message || 'تعذر بدء ربط Zoom');
    }
  }

  async getIntegrationStatus(): Promise<ZoomIntegrationStatus> {
    try {
      const getZoomIntegrationStatus = httpsCallable(
        functions,
        'getZoomIntegrationStatus'
      );
      const result = await getZoomIntegrationStatus({});
      return result.data as ZoomIntegrationStatus;
    } catch (error: any) {
      console.error('خطأ في قراءة حالة ربط Zoom:', error);
      throw new Error(error.message || 'تعذر قراءة حالة ربط Zoom');
    }
  }

  async disconnectAccount(): Promise<void> {
    try {
      const disconnectZoomAccount = httpsCallable(
        functions,
        'disconnectZoomAccount'
      );
      await disconnectZoomAccount({});
    } catch (error: any) {
      console.error('خطأ في إلغاء ربط Zoom:', error);
      throw new Error(error.message || 'تعذر إلغاء ربط Zoom');
    }
  }

  // إنشاء اجتماع جديد
  async createMeeting(
    meetingData: CreateMeetingRequest,
    classId?: string
  ): Promise<CreateMeetingResponse> {
    try {
      const createZoomMeeting = httpsCallable(functions, 'createZoomMeeting');
      const result = await createZoomMeeting({ meetingData, classId });
      return result.data as CreateMeetingResponse;
    } catch (error: any) {
      console.error('خطأ في إنشاء الاجتماع:', error);
      throw new Error(error.message || 'فشل في إنشاء الاجتماع');
    }
  }

  // الحصول على معلومات اجتماع
  async getMeeting(meetingId: string): Promise<GetMeetingResponse> {
    try {
      const getZoomMeeting = httpsCallable(functions, 'getZoomMeeting');
      const result = await getZoomMeeting({ meetingId });
      return result.data as GetMeetingResponse;
    } catch (error: any) {
      console.error('خطأ في الحصول على الاجتماع:', error);
      throw new Error(error.message || 'فشل في الحصول على الاجتماع');
    }
  }

  // حذف اجتماع
  async deleteMeeting(meetingId: string): Promise<void> {
    try {
      const deleteZoomMeeting = httpsCallable(functions, 'deleteZoomMeeting');
      await deleteZoomMeeting({ meetingId });
    } catch (error: any) {
      console.error('خطأ في حذف الاجتماع:', error);
      throw new Error(error.message || 'فشل في حذف الاجتماع');
    }
  }

  // الحصول على المشاركين
  async getMeetingParticipants(
    meetingId: string
  ): Promise<GetParticipantsResponse> {
    try {
      const getZoomMeetingParticipants = httpsCallable(
        functions,
        'getZoomMeetingParticipants'
      );
      const result = await getZoomMeetingParticipants({ meetingId });
      return result.data as GetParticipantsResponse;
    } catch (error: any) {
      console.error('خطأ في الحصول على المشاركين:', error);
      throw new Error(error.message || 'فشل في الحصول على المشاركين');
    }
  }

  // الحصول على التسجيلات
  async getMeetingRecordings(
    meetingId: string
  ): Promise<GetRecordingsResponse> {
    try {
      const getZoomMeetingRecordings = httpsCallable(
        functions,
        'getZoomMeetingRecordings'
      );
      const result = await getZoomMeetingRecordings({ meetingId });
      return result.data as GetRecordingsResponse;
    } catch (error: any) {
      console.error('خطأ في الحصول على التسجيلات:', error);
      throw new Error(error.message || 'فشل في الحصول على التسجيلات');
    }
  }

  // فتح اجتماع في نافذة جديدة
  openMeeting(joinUrl: string): void {
    window.open(joinUrl, '_blank', 'noopener,noreferrer');
  }

  // نسخ رابط الاجتماع
  async copyMeetingUrl(joinUrl: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(joinUrl);
    } catch (error) {
      // fallback للمتصفحات القديمة
      const textArea = document.createElement('textarea');
      textArea.value = joinUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  // تنسيق التاريخ والوقت
  formatMeetingTime(startTime: string | Date): string {
    const date = new Date(startTime);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Cairo',
    };

    return date.toLocaleDateString('ar-EG', options);
  }

  // حساب مدة الاجتماع بالدقائق
  calculateDuration(startTime: string, endTime: string): number {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  }

  // التحقق من حالة الاجتماع
  getMeetingStatus(meeting: StoredMeeting): 'upcoming' | 'live' | 'ended' {
    const now = new Date();
    const startTime = new Date(meeting.start_time);
    const endTime = new Date(startTime.getTime() + meeting.duration * 60000);

    if (now < startTime) {
      return 'upcoming';
    } else if (now >= startTime && now <= endTime) {
      return 'live';
    } else {
      return 'ended';
    }
  }

  // تحويل ملفات التسجيل حسب النوع
  categorizeRecordings(recordings: any[]) {
    return {
      videos: recordings.filter(r => r.file_type === 'MP4'),
      audios: recordings.filter(r => r.file_type === 'M4A'),
      chats: recordings.filter(r => r.file_type === 'CHAT'),
      transcripts: recordings.filter(r => r.file_type === 'TRANSCRIPT'),
    };
  }
}

export default new ZoomService();
